import { useState, useEffect, useCallback } from "react";
import * as Sentry from "@sentry/react";
import { authClient, type User, type Session, authHelpers } from '@/lib/authClient';
import type { AuthContextType } from "./AuthContext";

export const useAuthProvider = (): AuthContextType => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(false);

  const checkSession = useCallback(async () => {
    try {
      const sessionData = await authClient.getSession();
      
      if (sessionData.data?.user) {
        setUser(sessionData.data.user);
        setSession(sessionData.data.session);
        
        // Update Sentry user context
        Sentry.setUser({
          id: sessionData.data.user.id,
          email: sessionData.data.user.email,
        });
      } else {
        setUser(null);
        setSession(null);
        Sentry.setUser(null);
      }
    } catch (error) {
      console.error('Session check failed:', error);
      setUser(null);
      setSession(null);
      Sentry.setUser(null);
    } finally {
      setInitialLoading(false);
    }
  }, []);

  useEffect(() => {
    checkSession();
    
    // Set up periodic session checking
    const interval = setInterval(checkSession, 5 * 60 * 1000); // Check every 5 minutes
    
    return () => clearInterval(interval);
  }, [checkSession]);

  // Request OTP to be sent to email
  const requestOTP = async (email: string) => {
    setLoading(true);
    try {
      const result = await authClient.emailOtp.sendVerificationOtp({
        email,
        type: 'sign-in',
      });
      
      setLoading(false);
      
      if (result.error) {
        return { 
          error: {
            message: authHelpers.getErrorMessage(result.error),
            code: result.error.code || 'unknown_error'
          }
        };
      }
      
      return { error: null };
    } catch (error) {
      setLoading(false);
      return { 
        error: {
          message: authHelpers.getErrorMessage(error),
          code: 'unknown_error'
        }
      };
    }
  };

  // Verify OTP and create session
  const verifyOtp = async (email: string, otp: string) => {
    setLoading(true);
    try {
      const result = await authClient.signIn.emailOtp({
        email,
        otp,
      });
      
      setLoading(false);
      
      if (result.error) {
        return { 
          error: {
            message: authHelpers.getErrorMessage(result.error),
            code: result.error.code || 'verification_failed'
          },
          session: null,
          user: null
        };
      }
      
      await checkSession();

      return {
        error: null,
        user: result.data.user
      };
    } catch (error) {
      setLoading(false);
      return { 
        error: {
          message: authHelpers.getErrorMessage(error),
          code: 'verification_failed'
        },
        user: null
      };
    }
  };

  // Resend OTP
  const resendOTP = async (email: string) => {
    setLoading(true);
    try {
      const result = await authClient.emailOtp.sendVerificationOtp({
        email,
        type: 'sign-in',
      });
      
      setLoading(false);
      
      if (result.error) {
        return { 
          error: {
            message: authHelpers.getErrorMessage(result.error),
            code: result.error.code || 'resend_failed'
          }
        };
      }
      
      return { error: null };
    } catch (error) {
      setLoading(false);
      return { 
        error: {
          message: authHelpers.getErrorMessage(error),
          code: 'resend_failed'
        }
      };
    }
  };

  const logout = async () => {
    setLoading(true);
    try {
      const result = await authClient.signOut();
      
      // Clear local state
      setUser(null);
      setSession(null);
      Sentry.setUser(null);
      
      setLoading(false);
      
      if (result.error) {
        return { 
          error: {
            message: authHelpers.getErrorMessage(result.error),
            code: result.error.code || 'logout_failed'
          }
        };
      }
      
      return { error: null };
    } catch (error) {
      setLoading(false);
      console.error('Logout error:', error);
      
      // Even if logout fails on server, clear local state
      setUser(null);
      setSession(null);
      Sentry.setUser(null);
      
      return { 
        error: {
          message: authHelpers.getErrorMessage(error),
          code: 'logout_failed'
        }
      };
    }
  };

  return {
    session,
    user,
    initialLoading,
    loading,
    requestOTP,
    resendOTP,
    verifyOtp,
    logout,
    checkSession,
  };
}; 